import { CSSProperties, lazy, Suspense } from 'react';
import { Bounce } from 'react-activity';

import { Tour } from '../types';
import { Theme as ITheme } from '../types/theme';
import useURLParam, { OverlayURLParam } from '../hooks/useURLParam/useURLParam';

// Each tour only ever renders one theme, so themes are code-split and
// loaded on demand instead of all being bundled into the main chunk.
const LegacyTheme = lazy(
  () =>
    import(/* webpackChunkName: "theme-legacy" */ './LegacyTheme/LegacyTheme')
);
const RentCafeTheme = lazy(
  () =>
    import(
      /* webpackChunkName: "theme-rentcafe" */ './RentCafeTheme/RentCafeTheme'
    )
);
const TourBuilderProTheme = lazy(
  () =>
    import(
      /* webpackChunkName: "theme-tb-pro" */ './TourBuilderProTheme/TourBuilderProTheme'
    )
);
const TourBuilderUnitTheme = lazy(
  () =>
    import(
      /* webpackChunkName: "theme-tb-unit" */ './TourBuilderUnitTheme/TourBuilderUnitTheme'
    )
);
const GreystarTheme = lazy(
  () =>
    import(
      /* webpackChunkName: "theme-greystar" */ './Greystar/Standard/GreystarTheme'
    )
);
const GreystarUnitTheme = lazy(
  () =>
    import(
      /* webpackChunkName: "theme-greystar-unit" */ './Greystar/Unit/GreystarUnitTheme'
    )
);
const TourBuilderEssentialsTheme = lazy(
  () =>
    import(
      /* webpackChunkName: "theme-tb-essentials" */ './TourBuilderEssentials/TourBuilderEssentialsTheme'
    )
);
// LivcorTheme has no default export, so the two named exports are adapted
// to the { default } shape React.lazy requires. Both variants share the
// same chunk name since they come from the same module.
const SquareLivcorTheme = lazy(() =>
  import(
    /* webpackChunkName: "theme-livcor" */ './LivcorTheme/LivcorTheme'
  ).then((module) => ({
    default: module.SquareLivcorTheme,
  }))
);
const RoundedLivcorTheme = lazy(() =>
  import(
    /* webpackChunkName: "theme-livcor" */ './LivcorTheme/LivcorTheme'
  ).then((module) => ({
    default: module.RoundedLivcorTheme,
  }))
);

type SupportedThemes = Record<ThemeKey, React.ComponentType<ThemeProps>>;

const SUPPORTED_THEMES: SupportedThemes = {
  v1: TourBuilderUnitTheme,
  'livcor-rounded': RoundedLivcorTheme,
  'livcor-square': SquareLivcorTheme,
  'tb-default': TourBuilderProTheme,
  'tb-legacy': LegacyTheme,
  greystar: GreystarTheme,
  'tb-unit-default': TourBuilderUnitTheme,
  'greystar-unit': GreystarUnitTheme,
  'tb-essentials': TourBuilderEssentialsTheme,
  rentcafe: RentCafeTheme,
};

const SUPPORTED_UNIT_THEMES: Partial<SupportedThemes> = {
  'tb-unit-default': TourBuilderUnitTheme,
  'greystar-unit': GreystarUnitTheme,
  rentcafe: RentCafeTheme,
};

export type ThemeKey =
  | 'v1'
  | 'livcor-rounded'
  | 'livcor-square'
  | 'tb-default'
  | 'tb-legacy'
  | 'greystar'
  | 'tb-unit-default'
  | 'greystar-unit'
  | 'tb-essentials'
  | 'rentcafe';

export interface ThemeProps {
  theme: ITheme;
  tour: Tour;
  cssVariables?: CSSProperties;
}

function ThemeNotSupported() {
  return (
    <h1
      style={{
        background: `var(--menu-background)`,
        color: `var(--menu-icon-color-default)`,
        position: 'absolute',
        width: '100%',
        textAlign: 'center',
        padding: 20,
      }}
    >
      This theme is not supported yet.
    </h1>
  );
}

function TourSuspended() {
  return (
    <div
      style={{
        background: `var(--menu-background)`,
        color: `var(--menu-icon-color-default)`,
        height: '100vh',
        position: 'static',
        textAlign: 'center',
        padding: 20,
        width: '100%',
      }}
    >
      <h1
        style={{
          marginTop: '20%',
          textTransform: 'uppercase',
        }}
      >
        403
      </h1>
      <h3
        style={{
          textTransform: 'uppercase',
        }}
      >
        there is a technical issue with your tour.
      </h3>
      <h3
        style={{
          textTransform: 'uppercase',
        }}
      >
        please contact us at (877) 658-0433
      </h3>
      <i className="fa fa-exclamation-triangle fa-5x"></i>
    </div>
  );
}

function ThemeLoading() {
  return (
    <div
      style={{
        alignItems: 'center',
        display: 'flex',
        height: '100vh',
        justifyContent: 'center',
        width: '100%',
      }}
    >
      <Bounce animating color="#727981" size={32} speed={1} />
    </div>
  );
}

export default function Theme({ tour, theme, cssVariables }: ThemeProps) {
  const themeDebug = useURLParam(
    OverlayURLParam.THEME_DEBUG
  ) as keyof typeof SUPPORTED_THEMES;

  if (tour.tourSuspended) return <TourSuspended />;

  let ThemeComponent: React.ComponentType<ThemeProps>;

  if (Boolean(themeDebug) && SUPPORTED_THEMES[themeDebug]) {
    ThemeComponent = SUPPORTED_THEMES[themeDebug];
  } else if (tour.tourType === 'unit') {
    ThemeComponent =
      SUPPORTED_UNIT_THEMES[theme.unitTemplate] ?? TourBuilderUnitTheme;
  } else if (!theme.template) {
    ThemeComponent = TourBuilderUnitTheme;
  } else {
    ThemeComponent = SUPPORTED_THEMES[theme.template] ?? ThemeNotSupported;
  }

  return (
    <Suspense fallback={<ThemeLoading />}>
      <ThemeComponent cssVariables={cssVariables} theme={theme} tour={tour} />
    </Suspense>
  );
}
